import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface MontyPayCustomer {
  name: string;
  email: string;
  phone?: string;
}

export interface CreateSessionRequest {
  amount: number;
  currency: string;
  customer: MontyPayCustomer;
  return_url: string;
  cancel_url: string;
  description: string;
  metadata?: Record<string, any>;
}

export interface CreateSessionResponse {
  success: boolean;
  session_id: string;
  order_id: string;
  redirect_url: string;
  return_url: string;
  order: {
    number: string;
    amount: string;
    currency: string;
    description: string;
  };
  customer: {
    name: string;
    email: string;
  };
}

export interface PaymentStatusResponse {
  success: boolean;
  status: 'settled' | 'success' | 'pending' | '3ds' | 'decline' | 'fail' | 'refund' | 'chargeback';
  payment_status: 'completed' | 'pending' | 'failed';
  session_id: string;
  order_id: string;
  transaction_id?: string;
  amount?: string;
  currency?: string;
  description?: string;
  customer?: {
    name: string;
    email: string;
  };
  decline_reason?: string;
  message?: string;
}

/**
 * Creates a MontyPay payment session
 */
export const createMontyPaySession = async (
  data: CreateSessionRequest,
  authToken: string
): Promise<CreateSessionResponse> => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/api/v1/data/payments/montypay/session`,
      data,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('MontyPay session creation error:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create payment session',
      hint: error.response?.data?.hint,
      details: error.response?.data?.details,
    };
  }
};

/**
 * Checks the payment status by order_id
 */
export const checkPaymentStatus = async (
  orderId: string,
  authToken: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/api/v1/data/payments/montypay/status`,
      {
        params: { order_id: orderId },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Payment status check error:', error);
    throw {
      message: error.response?.data?.error || 'Failed to check payment status',
      details: error.response?.data?.details,
    };
  }
};

/**
 * Checks the payment status by session_id
 */
export const checkPaymentStatusBySession = async (
  sessionId: string,
  authToken: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/api/v1/data/payments/montypay/session/${sessionId}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Payment status check error:', error);
    throw {
      message: error.response?.data?.error || 'Failed to check payment status',
      details: error.response?.data?.details,
    };
  }
};

/**
 * DEPRECATED: Polling is no longer needed.
 * MontyPay only redirects to success_url when payment is successful.
 * Use checkPaymentStatus directly instead.
 * 
 * This function is kept for backward compatibility but just calls checkPaymentStatus once.
 */
export const pollPaymentStatus = async (
  orderId: string,
  authToken: string,
  maxAttempts: number = 5,
  delayMs: number = 2000
): Promise<PaymentStatusResponse> => {
  // No polling needed - single call is sufficient
  // MontyPay redirects to success_url only when payment is successful
  return checkPaymentStatus(orderId, authToken);
};

/**
 * Stores payment session data in localStorage
 */
export const storePaymentSession = (sessionId: string, orderId: string) => {
  localStorage.setItem('montypay_session_id', sessionId);
  localStorage.setItem('montypay_order_id', orderId);
};

/**
 * Retrieves payment session data from localStorage
 */
export const getStoredPaymentSession = () => {
  return {
    sessionId: localStorage.getItem('montypay_session_id'),
    orderId: localStorage.getItem('montypay_order_id'),
  };
};

/**
 * Clears payment session data from localStorage
 */
export const clearPaymentSession = () => {
  localStorage.removeItem('montypay_session_id');
  localStorage.removeItem('montypay_order_id');
};

/**
 * Updates the organization plan to "custom" after successful payment
 */
export const updateOrganizationPlan = async (
  orgId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
      {
        plan: {
          plan_name: 'custom',
          plan_description: 'Custom Plan - Full Access',
          plan_type: 'custom',
          status: 'active',
        },
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Organization plan update error:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update organization plan',
      details: error.response?.data?.details,
    };
  }
};
